import arrayTools from "_src/engine/utility/arrays";
import {Geometry, GeometryBuffer} from "_src/engine/geometry";
import {bufferType} from "_src/engine/constants";
import downloader from "_src/engine/loaders/downloader";

export const LoaderDataType = {
  TEXT: 0,
  BINARY: 1,
  STREAM: 2,
}

export class ObjectData {
  constructor(geometry, material) {
    this.geometry = geometry;
    this.material = material;
  }
}

export class GeometryRawData {
  constructor(positions) {
    this.positions = positions;
    this.indices = null;
    this.normals = null;
    this.uvs = null;
  }
}

export class Material {
  constructor() {
    this.flipTexture = true;
    this.baseTexture = null;
    this.metallicRoughnessTexture = null;
  }
}

export default class ExternalLoader {
  constructor() {
    this._basePath = "";
    this._dataType = LoaderDataType.BINARY;
  }

  async load(path, controller, useWorkers = false, progress = null) {
    this._basePath = path.substring(0, path.lastIndexOf("/")) + "/";

    const loadingProgress = progress ? (progressVal) => progress.emit(progress.LOADING, progressVal / 2) : null;
    const dataType = _loaderTypeToDownloaderType(this._dataType);
    const data = await downloader.download(path, controller, dataType, loadingProgress);
    const objectsRawData = await this._parse(data, progress, useWorkers);
    const objectsData = _createGeometry(objectsRawData);

    return objectsData;
  }

  async _parse() {
    throw new Error("ExternalLoader is an abstract class. Reimplement parsing function");
  }
}

function _loaderTypeToDownloaderType(loaderType) {
  switch (loaderType) {
    case LoaderDataType.TEXT:
      return downloader.TEXT;
    case LoaderDataType.BINARY:
      return downloader.BINARY;
    case LoaderDataType.STREAM:
      return downloader.READER;
  }
}

function _getTypedArray(array, constructor) {
  let typedArray;
  if (array.buffer instanceof ArrayBuffer) {
    typedArray = array;
  } else {
    typedArray = new constructor(array.length);
    typedArray.set(array);
  }

  return typedArray;
}

function _createGeometry(objectsRawData) {
  const objectsData = [];
  for (const objectRawData of objectsRawData) {
    const rawGeometry = objectRawData.geometry;
    const positions = rawGeometry.positions;
    const normals = rawGeometry.normals;
    const uvs = rawGeometry.uvs;
    const indices = rawGeometry.indices && rawGeometry.indices.length > 0 ? rawGeometry.indices : _generateDefaultIndices(positions.length / 3);
    const bbox = _getBbox(positions);

    const layout = {};

    const maxValue = positions.length / 3 - 1;
    const indicesArrayConstructor = arrayTools.uintArrayTypeByMaxValue(maxValue);
    const indicesTypedArray = _getTypedArray(indices, indicesArrayConstructor);
    const indType = arrayTools.uintArrayType(indicesTypedArray, 3);
    const indicesItem = new GeometryBuffer({buffer: indicesTypedArray.buffer, type: indType});
    layout["indices"] = indicesItem;

    if (positions && positions.length) {
      const posTypedArray = _getTypedArray(positions, Float32Array);
      const positionItem = new GeometryBuffer({buffer: posTypedArray.buffer, type: bufferType.F32_3});
      layout["a_position"] = positionItem;
    }

    if (normals && normals.length) {
      const normTypedArray = _getTypedArray(normals, Float32Array);
      const normalItem = new GeometryBuffer({buffer: normTypedArray.buffer, type: bufferType.F32_3});
      layout["a_normal"] = normalItem;
    }

    if (uvs && uvs.length) {
      const uvTypedArray = _getTypedArray(uvs, Float32Array);
      const uvItem = new GeometryBuffer({buffer: uvTypedArray.buffer, type: bufferType.F32_2});
      layout["a_uv"] = uvItem;
    }

    const geometry = new Geometry({layout, bbox});
    const objectData = new ObjectData(geometry, objectRawData.material);

    objectsData.push(objectData);
  }

  return objectsData;
}

function _generateDefaultIndices(count) {
  const res = new Uint32Array(count);
  for (let i=0; i<count; i++) {
    res[i] = i;
  }

  return res;
}

function _getBbox(positions) {
  const bbox = [Infinity, Infinity, Infinity, -Infinity, -Infinity, -Infinity];

  for (let i=0; i<positions.length; i+=3) {
    const x = positions[i];
    const y = positions[i+1];
    const z = positions[i+2];

    bbox[0] = Math.min(bbox[0], x);
    bbox[1] = Math.min(bbox[1], y);
    bbox[2] = Math.min(bbox[2], z);

    bbox[3] = Math.max(bbox[3], x);
    bbox[4] = Math.max(bbox[4], y);
    bbox[5] = Math.max(bbox[5], z);
  }

  return bbox;
}
